import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../services/firebase';
import { toast } from 'react-toastify';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import Credentials from '../components/Credentials';
import './Profile.css';

const Profile = () => {
  const { currentUser, userProfile, loadUserProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  // Teacher profil verileri
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    bio: '',
    hourlyRate: 0,
    subjects: [],
    languages: [],
    availability: {},
    education: [],
    experience: [],
    certifications: []
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  useEffect(() => {
    if (currentUser && userProfile) {
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, userProfile?.role]); // Sadece uid ve role değişince çalışsın

  const loadProfile = async () => {
    try {
      // User bilgileri
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setProfileData(prev => ({
          ...prev,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          phoneNumber: userData.phoneNumber || ''
        }));
      }

      // Eğer öğretmense, teacher bilgileri
      if (userProfile.role === 'teacher') {
        const teacherDoc = await getDoc(doc(db, 'teachers', currentUser.uid));
        if (teacherDoc.exists()) {
          const teacherData = teacherDoc.data();
          setProfileData(prev => ({
            ...prev,
            bio: teacherData.bio || '',
            hourlyRate: teacherData.hourlyRate || 0,
            subjects: teacherData.subjects || [],
            languages: teacherData.languages || [],
            availability: teacherData.availability || {},
            education: teacherData.education || [],
            experience: teacherData.experience || [],
            certifications: teacherData.certifications || []
          }));
          setPhotoPreview(teacherData.photoURL);
        }
      }
    } catch (error) {
      console.error('Load profile error:', error);
      toast.error('Failed to load profile');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo must be less than 5MB');
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Video must be less than 50MB');
        return;
      }
      setVideoFile(file);
      toast.success('Video selected. Will upload when you save.');
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return null;
    
    try {
      const storageRef = ref(storage, `profiles/${currentUser.uid}/photo.jpg`);
      await uploadBytes(storageRef, photoFile);
      const photoURL = await getDownloadURL(storageRef);
      return photoURL;
    } catch (error) {
      console.error('Photo upload error:', error);
      throw error;
    }
  };

  const uploadVideo = async () => {
    if (!videoFile) return null;
    
    try {
      const storageRef = ref(storage, `videos/${currentUser.uid}/intro.mp4`);
      await uploadBytes(storageRef, videoFile);
      const videoURL = await getDownloadURL(storageRef);
      return videoURL;
    } catch (error) {
      console.error('Video upload error:', error);
      throw error;
    }
  };

  const handleSaveBasic = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Fotoğraf yükle
      let photoURL = photoPreview;
      if (photoFile) {
        photoURL = await uploadPhoto();
        toast.success('Photo uploaded!');
      }

      // User bilgilerini güncelle
      await updateDoc(doc(db, 'users', currentUser.uid), {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phoneNumber: profileData.phoneNumber,
        updatedAt: new Date().toISOString()
      });

      // Eğer öğretmense teacher bilgilerini güncelle
      if (userProfile.role === 'teacher') {
        await updateDoc(doc(db, 'teachers', currentUser.uid), {
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          bio: profileData.bio,
          photoURL: photoURL,
          updatedAt: new Date().toISOString()
        });
      }

      await loadUserProfile(currentUser.uid);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTeaching = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Video yükle
      let videoURL = null;
      if (videoFile) {
        videoURL = await uploadVideo();
        toast.success('Video uploaded!');
      }

      const updateData = {
        hourlyRate: parseFloat(profileData.hourlyRate),
        subjects: profileData.subjects,
        languages: profileData.languages,
        updatedAt: new Date().toISOString()
      };

      if (videoURL) {
        updateData.videoIntroUrl = videoURL;
      }

      await updateDoc(doc(db, 'teachers', currentUser.uid), updateData);

      toast.success('Teaching info updated!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to update teaching info');
    } finally {
      setLoading(false);
    }
  };

  const addSubject = (subject) => {
    if (!profileData.subjects.includes(subject)) {
      setProfileData(prev => ({
        ...prev,
        subjects: [...prev.subjects, subject]
      }));
    }
  };

  const removeSubject = (subject) => {
    setProfileData(prev => ({
      ...prev,
      subjects: prev.subjects.filter(s => s !== subject)
    }));
  };

  const addLanguage = (language) => {
    if (!profileData.languages.includes(language)) {
      setProfileData(prev => ({
        ...prev,
        languages: [...prev.languages, language]
      }));
    }
  };

  const removeLanguage = (language) => {
    setProfileData(prev => ({
      ...prev,
      languages: prev.languages.filter(l => l !== language)
    }));
  };

  const availableSubjects = [
    'English', 'Mathematics', 'Science', 'Programming', 'Music', 
    'Art', 'Business', 'Spanish', 'French', 'German', 'Turkish',
    'Physics', 'Chemistry', 'Biology', 'History', 'Geography'
  ];

  const availableLanguages = [
    'English', 'Turkish', 'Spanish', 'French', 'German', 
    'Russian', 'Italian', 'Arabic', 'Chinese', 'Portuguese'
  ];

  if (!currentUser) {
    return (
      <div className="container" style={{ padding: '80px 20px', textAlign: 'center' }}>
        <h2>Please log in to view your profile</h2>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="container" style={{ padding: '80px 20px', textAlign: 'center' }}>
        <div className="spinner"></div>
        <p style={{ marginTop: '20px' }}>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="container">
        <div className="profile-header">
          <h1>My Profile</h1>
          <p>Manage your profile and preferences</p>
        </div>

        <div className="profile-container">
          {/* Tabs */}
          <div className="profile-tabs">
            <button
              className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
              onClick={() => setActiveTab('basic')}
            >
              Basic Info
            </button>
            
            {userProfile.role === 'teacher' && (
              <>
                <button
                  className={`tab-btn ${activeTab === 'teaching' ? 'active' : ''}`}
                  onClick={() => setActiveTab('teaching')}
                >
                  Teaching Info
                </button>
                <button
                  className={`tab-btn ${activeTab === 'availability' ? 'active' : ''}`}
                  onClick={() => setActiveTab('availability')}
                >
                  Availability
                </button>
                <button
                  className={`tab-btn ${activeTab === 'credentials' ? 'active' : ''}`}
                  onClick={() => setActiveTab('credentials')}
                >
                  Credentials
                </button>
              </>
            )}
          </div>

          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <form onSubmit={handleSaveBasic} className="profile-form">
              <div className="form-section">
                <h2>Profile Photo</h2>
                <div className="photo-upload">
                  <div className="photo-preview">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Profile" />
                    ) : (
                      <div className="photo-placeholder">
                        {profileData.firstName?.charAt(0) || 'U'}
                      </div>
                    )}
                  </div>
                  <div className="photo-actions">
                    <input
                      type="file"
                      id="photo-upload"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="photo-upload" className="btn btn-secondary">
                      📷 Upload Photo
                    </label>
                    <p className="help-text">Max 5MB, JPG or PNG</p>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h2>Personal Information</h2>
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={profileData.lastName}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    value={profileData.email}
                    className="form-input"
                    disabled
                  />
                  <p className="help-text">Email cannot be changed</p>
                </div>

                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={profileData.phoneNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="+44 20 1234 5678"
                  />
                </div>

                {userProfile.role === 'teacher' && (
                  <div className="form-group">
                    <label className="form-label">About Me</label>
                    <textarea
                      name="bio"
                      value={profileData.bio}
                      onChange={handleInputChange}
                      className="form-textarea"
                      rows="6"
                      placeholder="Tell students about yourself, your teaching style, and experience..."
                    />
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? <div className="spinner"></div> : '💾 Save Changes'}
                </button>
              </div>
            </form>
          )}

          {/* Teaching Info Tab */}
          {activeTab === 'teaching' && userProfile.role === 'teacher' && (
            <form onSubmit={handleSaveTeaching} className="profile-form">
              <div className="form-section">
                <h2>Hourly Rate</h2>
                <div className="form-group">
                  <label className="form-label">Price per Hour (£) *</label>
                  <input
                    type="number"
                    name="hourlyRate"
                    value={profileData.hourlyRate}
                    onChange={handleInputChange}
                    className="form-input"
                    min="5"
                    max="500"
                    step="0.01"
                    required
                  />
                  <p className="help-text">Set your hourly rate in British Pounds (£)</p>
                </div>
              </div>

              <div className="form-section">
                <h2>Subjects I Teach</h2>
                <div className="selected-items">
                  {profileData.subjects.map((subject, index) => (
                    <span key={index} className="item-tag">
                      {subject}
                      <button
                        type="button"
                        onClick={() => removeSubject(subject)}
                        className="remove-btn"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="available-items">
                  {availableSubjects
                    .filter(s => !profileData.subjects.includes(s))
                    .map((subject, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => addSubject(subject)}
                        className="add-item-btn"
                      >
                        + {subject}
                      </button>
                    ))}
                </div>
              </div>

              <div className="form-section">
                <h2>Languages I Speak</h2>
                <div className="selected-items">
                  {profileData.languages.map((language, index) => (
                    <span key={index} className="item-tag">
                      {language}
                      <button
                        type="button"
                        onClick={() => removeLanguage(language)}
                        className="remove-btn"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="available-items">
                  {availableLanguages
                    .filter(l => !profileData.languages.includes(l))
                    .map((language, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => addLanguage(language)}
                        className="add-item-btn"
                      >
                        + {language}
                      </button>
                    ))}
                </div>
              </div>

              <div className="form-section">
                <h2>Introduction Video</h2>
                <div className="video-upload">
                  <input
                    type="file"
                    id="video-upload"
                    accept="video/*"
                    onChange={handleVideoChange}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="video-upload" className="btn btn-secondary">
                    🎥 Upload Video
                  </label>
                  <p className="help-text">Max 50MB, MP4 recommended. A short video introducing yourself helps students connect with you!</p>
                  {videoFile && (
                    <p className="text-success">✅ Video selected: {videoFile.name}</p>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? <div className="spinner"></div> : '💾 Save Teaching Info'}
                </button>
              </div>
            </form>
          )}

          {/* Availability Tab */}
          {activeTab === 'availability' && userProfile.role === 'teacher' && (
            <div className="profile-form">
              <AvailabilityCalendar
                teacherId={currentUser.uid}
                initialAvailability={profileData.availability}
              />
            </div>
          )}

          {/* Credentials Tab */}
          {activeTab === 'credentials' && userProfile.role === 'teacher' && (
            <div className="profile-form">
              <Credentials
                teacherId={currentUser.uid}
                initialData={{
                  education: profileData.education,
                  experience: profileData.experience,
                  certifications: profileData.certifications
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;